import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { Breadcrumbs, Box, Chip, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { DynamicBreadcrumbsProps } from './Breadcrumb.types';

const chipLabelNoWrapSx = {
    display: 'block',
    whiteSpace: 'nowrap',
} as const;

/** Scroll so the end of the trail (active crumb) stays in view. */
function scrollBreadcrumbToEnd(container: HTMLElement | null) {
    if (!container) return;
    container.scrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
}

export const DynamicBreadcrumbs: React.FC<DynamicBreadcrumbsProps> = ({ items }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();

    // PROFESSIONAL light remaps secondary.main to a light grey, which makes the default
    // chip (primary.main bg + secondary.main text) unreadable. Use breadcrumb palette
    // tokens when present, otherwise fall back to the original primary/secondary scheme.
    const bc = theme.palette.breadcrumb;
    const linkBadgeSx = {
        height: 'auto',
        minHeight: 22,
        backgroundColor: bc?.linkBg ?? 'primary.main',
        color: bc?.linkColor ?? 'secondary.main',
        fontWeight: 600,
        fontSize: '0.75rem',
        borderRadius: 1,
        border: '1px solid',
        borderColor: bc?.linkBorderColor ?? 'secondary.main',
        '& .MuiChip-label': {
            px: 1,
            py: 0.15,
        },
        '&:hover': {
            backgroundColor: bc?.linkHoverBg ?? 'primary.dark',
            borderColor: bc?.linkBorderColor ?? 'secondary.main',
        },
    } as const;

    const displayItems = useMemo(() => (items.length === 0 ? [] : items), [items]);

    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        scrollBreadcrumbToEnd(el);
        const ro = new ResizeObserver(() => scrollBreadcrumbToEnd(el));
        ro.observe(el);
        return () => ro.disconnect();
    }, [displayItems]);

    if (displayItems.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                mb: 1.5,
                px: 1,
                py: 0.5,
                borderRadius: 1.5,
                backgroundColor: (t) =>
                    t.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
                minWidth: 0,
                maxWidth: '100%',
            }}
        >
            <Box
                ref={scrollRef}
                sx={{
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    width: '100%',
                    maxWidth: '100%',
                    WebkitOverflowScrolling: 'touch',
                    // Firefox
                    scrollbarWidth: 'thin',
                }}
            >
                <Breadcrumbs
                    aria-label="breadcrumb"
                    separator=">"
                    sx={{
                        width: 'max-content',
                        maxWidth: 'none',
                        flexWrap: 'nowrap',
                        '& ol': {
                            flexWrap: 'nowrap',
                            whiteSpace: 'nowrap',
                        },
                        '& li': {
                            flexShrink: 0,
                        },
                        '& .MuiBreadcrumbs-separator': {
                            mx: 0.4,
                            color: 'text.secondary',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            lineHeight: 1,
                            flexShrink: 0,
                        },
                    }}
                >
                    {displayItems.map((item, index) => {
                        const isActiveCrumb = index === displayItems.length - 1;

                        return isActiveCrumb ? (
                            <Chip
                                key={`${item.itemPath}-last`}
                                label={item.itemLabel}
                                sx={{
                                    ...linkBadgeSx,
                                    backgroundColor: bc?.activeBg ?? 'primary.main',
                                    color: bc?.activeColor ?? linkBadgeSx.color,
                                    flexShrink: 0,
                                    maxWidth: 'none',
                                    cursor: 'default',
                                    pointerEvents: 'none',
                                    borderColor: 'transparent',
                                    '& .MuiChip-label': {
                                        ...linkBadgeSx['& .MuiChip-label'],
                                        ...chipLabelNoWrapSx,
                                    },
                                    '&:hover': {
                                        backgroundColor: bc?.activeBg ?? 'primary.main',
                                        borderColor: 'transparent',
                                    },
                                }}
                            />
                        ) : (
                            <Chip
                                key={item.itemPath}
                                component={RouterLink}
                                to={item.itemPath}
                                state={item.itemState}
                                label={item.itemLabel}
                                clickable
                                sx={{
                                    ...linkBadgeSx,
                                    flexShrink: 0,
                                    maxWidth: 'none',
                                    '& .MuiChip-label': {
                                        ...linkBadgeSx['& .MuiChip-label'],
                                        ...chipLabelNoWrapSx,
                                    },
                                }}
                            />
                        );
                    })}
                </Breadcrumbs>
            </Box>
        </Box>
    );
};
